import { Unless } from 'react-if';
import { PersonStackFillIcon } from '@zep-us/zus-icons';
import Link from 'next/link';
import { useRouter } from 'next/router';
import { useTranslation } from 'next-i18next';
import { useAuth } from '@zep/auth';
import { SubscribePlan } from '@zep/types';
import { Button } from '@zep/ui';
import { Colors } from '@zep/ui-tokens';
import { fDateTimeByCountryCode } from '@zep/utils';
import { usePlanSpaceDetail } from '../../../../services';
import { BoltFillIcon, ExportIcon } from '../../../icons';
import { PlanTypeLabel } from '../components';
import { FormLayout } from '../space/organisms/FormLayout/FormLayout';
import { PlanSpaceGroup } from '../space/organisms/PlanSpaceGroup/PlanSpaceGroup';
import { SUBSCRIPTION_FEATURES, SUBSCRIPTION_FEATURES_JA } from '../SubscriptionConst';
import { NoticeBox } from './atoms';
import S from './page.module.scss';
export const CancelInformation = (props: CancelInformationProps) => {
  const {
    t
  } = useTranslation(['subscription']);
  const {
    onCancelClick
  } = props;
  const {
    user
  } = useAuth();
  const {
    query
  } = useRouter();
  const {
    data: {
      spacePlan,
      space
    }
  } = usePlanSpaceDetail(query.id as string);
  const features = space.countryCode === 'JP' ? SUBSCRIPTION_FEATURES_JA[spacePlan?.planType?.toLowerCase() as SubscribePlan] : SUBSCRIPTION_FEATURES[spacePlan?.planType?.toLowerCase() as SubscribePlan];
  return <FormLayout subtitle={t('subscription:unsubscribe.layout.subtitle1', {
    name: user!.name
  })} title={t('subscription:unsubscribe.layout.title1')} data-sentry-element="FormLayout" data-sentry-component="CancelInformation" data-sentry-source-file="CancelInformation.tsx">
      {/* 스페이스명 */}
      <PlanSpaceGroup title={t('subscription:unsubscribe.groupTitle.spaceName')} data-sentry-element="PlanSpaceGroup" data-sentry-source-file="CancelInformation.tsx">
        <Link href={`/play/${space?.entryMapHashId}`} target="_blank" className={S.space_link} prefetch={false} data-sentry-element="Link" data-sentry-source-file="CancelInformation.tsx">
          <span>{space?.name}</span>
          <ExportIcon width={18} height={18} color={Colors.Gray600} data-sentry-element="ExportIcon" data-sentry-source-file="CancelInformation.tsx" />
        </Link>
      </PlanSpaceGroup>

      {/* 현재플랜 */}
      <PlanSpaceGroup title={t('subscription:unsubscribe.groupTitle.currentPlan')} data-sentry-element="PlanSpaceGroup" data-sentry-source-file="CancelInformation.tsx">
        <div className={S.current_plan}>
          <PlanTypeLabel planType={spacePlan?.planType} maxCcu={spacePlan?.maxCcu} data-sentry-element="PlanTypeLabel" data-sentry-source-file="CancelInformation.tsx" />
          <div className={S.recommend_plan}>
            <p>{t('subscription:unsubscribe.currentPlan.description')}</p>

            {/* 다른 플랜 알아보기 */}
            <Link href={`/plan-payment?spaceHashId=${query.id}`} passHref legacyBehavior prefetch={false} data-sentry-element="Link" data-sentry-source-file="CancelInformation.tsx">
              <Button as="a" size="xs" variant="secondary" target="_blank" data-sentry-element="Button" data-sentry-source-file="CancelInformation.tsx">
                {t('subscription:unsubscribe.seeOtherPlanButton.text')}
              </Button>
            </Link>
          </div>
        </div>
      </PlanSpaceGroup>

      {/* 플랜 제공 기능 */}
      <PlanSpaceGroup title={t('subscription:unsubscribe.groupTitle.features')} data-sentry-element="PlanSpaceGroup" data-sentry-source-file="CancelInformation.tsx">
        <p className={S.benefit_text}>
          <BoltFillIcon width={18} height={18} color={Colors.PrimaryDefault} data-sentry-element="BoltFillIcon" data-sentry-source-file="CancelInformation.tsx" />
          <span>{t('subscription:unsubscribe.features.description')}</span>
        </p>

        <div className={S.benefit_area}>
          <p dangerouslySetInnerHTML={{
          __html: t('subscription:unsubscribe.features.description2', {
            name: user!.name
          })
        }} />
          <ul>
            <li>
              <PersonStackFillIcon width={12} height={12} color={Colors.Gray600} data-sentry-element="PersonStackFillIcon" data-sentry-source-file="CancelInformation.tsx" />
              <span>
                {spacePlan?.maxCcu !== undefined && spacePlan?.maxCcu < 0 ? t('subscription:SpaceDetail.menuManage.currentPlanMaxCcu') : t('subscription:SpaceDetail.menuManage.currentPlanCcu', {
                ccu: spacePlan?.maxCcu ?? 0
              })}
              </span>
            </li>
            {features.map(feature => <li key={feature.key}>
                {feature.icon({
              width: 12,
              height: 12,
              fill: Colors.Gray600
            })}
                <span>{t(feature.key)}</span>
              </li>)}
          </ul>
        </div>
      </PlanSpaceGroup>

      {/* 유의사항 */}
      <PlanSpaceGroup title={t('subscription:unsubscribe.groupTitle.cautions')} data-sentry-element="PlanSpaceGroup" data-sentry-source-file="CancelInformation.tsx">
        <NoticeBox title={t('subscription:unsubscribe.importantNotice.title')} data-sentry-element="NoticeBox" data-sentry-source-file="CancelInformation.tsx">
          <ul>
            <li>{t('subscription:unsubscribe.importantNotice.item1')}</li>
            <Unless condition={(spacePlan?.paymentFailCount ?? 0) > 0} data-sentry-element="Unless" data-sentry-source-file="CancelInformation.tsx">
              <li dangerouslySetInnerHTML={{
              __html: t('subscription:unsubscribe.importantNotice.item2', {
                date: fDateTimeByCountryCode(spacePlan?.endDate!, space?.countryCode)
              })
            }} />
              <li>{t('subscription:unsubscribe.importantNotice.item3')}</li>
            </Unless>
          </ul>
        </NoticeBox>
      </PlanSpaceGroup>

      {/* 구독 해지 및 취소 버튼 영역 */}
      <PlanSpaceGroup className="mt-[20px]" data-sentry-element="PlanSpaceGroup" data-sentry-source-file="CancelInformation.tsx">
        <div className={S.button_area}>
          {/* 혜택 유지하기 */}
          <Link href={`/me/spaces/${query.id}/plan`} scroll={false} passHref legacyBehavior prefetch={false} data-sentry-element="Link" data-sentry-source-file="CancelInformation.tsx">
            <Button as="a" size="md" data-sentry-element="Button" data-sentry-source-file="CancelInformation.tsx">
              {t('subscription:unsubscribe.keepBenefitButton.text')}
            </Button>
          </Link>

          {/* 취소 */}
          <Button size="md" variant="sub" transparent onClick={() => onCancelClick?.()} data-sentry-element="Button" data-sentry-source-file="CancelInformation.tsx">
            {t('subscription:unsubscribe.cancelRequestButton.text')}
          </Button>
        </div>
      </PlanSpaceGroup>
    </FormLayout>;
};
type CancelInformationProps = {
  onCancelClick?: () => void;
};